import { alpha, Box, Button, Checkbox, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Toolbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ListItemBackground, PageHeader, TableHeader } from '../../app/AppStyles';
import { SearchKeyFilter } from '../../app/AppUtils';
import SearchField from '../subcomponents/SearchField';
import TablePaginationActions from '../subcomponents/TablePaginationActions';
import { setMsgReceivers, setMsgStudentFilter } from './messageSlice';

export default function MessagesStudents({ selectedStudents, setSelectedStudents }) {
    const dispatch = useDispatch();

    const { msgStudents, msgStudentsFilter } = useSelector((state) => state.messages);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        setPage(0);
    }, [msgStudents]);

    const handleFilterResult = (item) => {
        return SearchKeyFilter(item, ["fullname", "studentNumber"], msgStudentsFilter);
    }

    function EnhancedTableHead({ onSelectAllClick, numSelected, rowCount }) {
        return (
            <TableHead>
                <TableRow>
                    <TableCell padding="checkbox">
                        <Checkbox
                            color="primary"
                            indeterminate={numSelected > 0 && (msgStudentsFilter ? numSelected < msgStudents?.filter(handleFilterResult).length : numSelected < rowCount)}
                            checked={rowCount > 0 && (msgStudentsFilter ? numSelected === msgStudents?.filter(handleFilterResult).length : numSelected === rowCount)}
                            onChange={onSelectAllClick}
                            inputProps={{ 'aria-label': 'select all' }}
                        />
                    </TableCell>
                    <TableCell align="center" sx={{ ...TableHeader, width: 100 }}>{resourceFinal[appLanguage].words.STUDENT}{resourceFinal[appLanguage].words.NUMBER.toLowerCase()}</TableCell>
                    <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.NAME}</TableCell>
                </TableRow>
            </TableHead>
        );
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked && msgStudents) {
            const newSelected = msgStudents?.filter(handleFilterResult)?.map((n) => n);
            setSelectedStudents(newSelected);
            return;
        };
        setSelectedStudents([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selectedStudents.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedStudents, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedStudents.slice(1));
        } else if (selectedIndex === selectedStudents.length - 1) {
            newSelected = newSelected.concat(selectedStudents.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedStudents.slice(0, selectedIndex),
                selectedStudents.slice(selectedIndex + 1),
            );
        };

        setSelectedStudents(newSelected);
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - msgStudents?.length) : 0;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (studentId) => selectedStudents.findIndex(x => x.studentId === studentId) !== -1;

    const handleFillClick = () => {
        dispatch(setMsgReceivers(selectedStudents));
    };

    return (
        <>
            <EnhancedTableToolbar numSelected={selectedStudents.length} fillFunc={handleFillClick} />
            <TableContainer>
                <Table>
                    <EnhancedTableHead numSelected={selectedStudents.length} onSelectAllClick={handleSelectAllClick} rowCount={msgStudents?.length} />
                    <TableBody>
                        {(rowsPerPage > 0 ? msgStudents?.filter(handleFilterResult)?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : msgStudents?.filter(handleFilterResult))?.map((item, index) => {
                            const isItemSelected = isSelected(item.studentId);
                            const labelId = `enhanced-table-checkbox-${index}`;
                            return (
                                <TableRow hover key={item.studentId} onClick={(event) => handleClick(event, item)} role="checkbox" aria-checked={isItemSelected} tabIndex={-1} selected={isItemSelected} sx={{ cursor: 'pointer' }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox color="primary" checked={isItemSelected} inputProps={{ 'aria-labelledby': labelId, }} />
                                    </TableCell>
                                    <TableCell align="center">{item.studentNumber}</TableCell>
                                    <TableCell align="left">{item.fullname}</TableCell>
                                </TableRow>
                            );
                        })}
                        {(rowsPerPage > 0 ? msgStudents?.filter(handleFilterResult)?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : msgStudents?.filter(handleFilterResult))?.length === 0 && <TableRow><TableCell colSpan={4}>{resourceFinal[appLanguage].messages.RESULT_EMPTY}</TableCell></TableRow>}
                        {emptyRows > 0 && <TableRow style={{ height: 53 * emptyRows }}><TableCell colSpan={4} /></TableRow>}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                sx={{ border: 0 }}
                                rowsPerPageOptions={[10, 25, 50]}
                                count={msgStudents ? msgStudents?.length : 0}
                                rowsPerPage={rowsPerPage} page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                                labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </>
    );
}

function EnhancedTableToolbar({ numSelected, fillFunc }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { postMessageStatus } = useSelector((state) => state.messages);
    const { msgStudentsFilter } = useSelector((state) => state.messages);

    useEffect(() => {
        if (postMessageStatus === "success")
            navigate(0);
    }, [dispatch, navigate, postMessageStatus]);

    return (
        <Toolbar disableGutters sx={{ px: 1, display: 'flex', justifyContent: 'space-between', ...(numSelected > 0 && { bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity) }) }}>
            <Box sx={{ width: 130 }}>
                {numSelected > 0 ? (
                    <Typography
                        color="inherit"
                        variant="subtitle1"
                        component="div"
                    >
                        {numSelected} {resourceFinal[appLanguage].words.SELECTED.toLowerCase()}
                    </Typography>
                ) : (
                    <Typography
                        sx={{ ...PageHeader, textAlign: 'left' }}
                        variant="h6"
                        id="tableTitle"
                        component="div"
                    >
                        {resourceFinal[appLanguage].words.STUDENTS}
                    </Typography>
                )}
            </Box>
            <SearchField initialValue={msgStudentsFilter} onDebouncedChange={(value) => dispatch(setMsgStudentFilter(value))} />
            {numSelected > 0 ? <Button variant="outlined" color="secondary" onClick={() => fillFunc()} sx={{ width: 130 }}>{resourceFinal[appLanguage].words.ADD}</Button> : <Box sx={{ width: 130 }}></Box>}
        </Toolbar>
    );
};